import { Box } from "@outschool/backpack";
import { useMutation } from "@outschool/ui-apollo";
import { Loading } from "@outschool/ui-legacy-component-library";
import React, { useCallback, useEffect, useState } from "react";

import {
  CreateEnrollmentFutureMeetingsMutation,
  CreateEnrollmentFutureMeetingsMutationVariables
} from "../../../shared/generated/graphql";
import { usePaymentMethods } from "../../hooks/CurrentUser";
import { EnrollmentMeetingCreateFutureMeetingsMutation } from "../../queries/EnrollmentMeetingCreateFutureMeetingsMutation";
import { SavePaymentMethodModal } from "../account/SavePaymentMethod";
import { EnrollmentMeetingPayLaterCheckout } from "./EnrollmentMeetingPayLaterCheckout";
import { EnrollmentMeetingPurchaseQueryType } from "./EnrollmentMeetingPurchaseWithStripe";

interface EnrollmentMeetingsWithoutPaymentProps {
  onCancel: () => void;
  onSubmit: (hasErrors: boolean) => void;
  activity: EnrollmentMeetingPurchaseQueryType["activity"];
  totalCents: number;
  sectionUid: string;
  startTime: Date;
  endTime: Date;
  durationWeeks: number | null;
  isRecurring: boolean;
  orgTeacherUid?: string;
  meetingRequestUids?: string[];
  recurringScheduleUid?: string;
  hasActiveSubscription?: boolean;
}
export const EnrollmentMeetingsWithoutPayment = ({
  activity,
  sectionUid,
  startTime,
  endTime,
  durationWeeks,
  orgTeacherUid,
  isRecurring,
  totalCents,
  onSubmit,
  onCancel,
  meetingRequestUids,
  recurringScheduleUid,
  hasActiveSubscription
}: EnrollmentMeetingsWithoutPaymentProps) => {
  const { loading: isPaymentMethodsLoading, paymentMethods } =
    usePaymentMethods();
  const [defaultPaymentMethod] = paymentMethods;

  const [isPaymentSaved, setIsPaymentSaved] = useState(false);

  useEffect(() => {
    if (!isPaymentMethodsLoading && !!defaultPaymentMethod) {
      setIsPaymentSaved(true);
    }
  }, [defaultPaymentMethod, isPaymentMethodsLoading]);

  const [createFutureMeetingsMutation, { loading }] = useMutation<
    CreateEnrollmentFutureMeetingsMutation,
    CreateEnrollmentFutureMeetingsMutationVariables
  >(EnrollmentMeetingCreateFutureMeetingsMutation, {
    onError(error) {
      OsPlatform.captureError(error);
    }
  });

  const handleSavePaymentComplete = async () => {
    setIsPaymentSaved(true);
  };

  const createFutureMeetings = useCallback(async () => {
    const variables = meetingRequestUids?.length
      ? {
          activityUid: activity.uid,
          sectionUid,
          startTime,
          meetingRequestUids,
          recurringScheduleUid,
          orgTeacherUid
        }
      : {
          activityUid: activity.uid,
          sectionUid,
          startTime,
          endTime,
          durationWeeks,
          isRecurring,
          orgTeacherUid,
          meetingRequestUids,
          recurringScheduleUid
        };
    const { errors } = await createFutureMeetingsMutation({
      variables
    });
    await onSubmit(!!errors);
  }, [
    activity.uid,
    sectionUid,
    startTime,
    meetingRequestUids,
    recurringScheduleUid,
    orgTeacherUid,
    endTime,
    durationWeeks,
    isRecurring,
    createFutureMeetingsMutation,
    onSubmit
  ]);

  useEffect(() => {
    if (hasActiveSubscription) {
      createFutureMeetings();
    }
  }, [createFutureMeetings, hasActiveSubscription]);

  if (hasActiveSubscription) {
    return <Loading />;
  }

  return (
    <Box
      flex
      sx={{
        width: "100%",
        flexDirection: "column"
      }}
    >
      {isPaymentSaved ? (
        <EnrollmentMeetingPayLaterCheckout
          onCancel={onCancel}
          onSubmit={createFutureMeetings}
          activity={activity}
          dueToday={0}
          totalCents={totalCents}
        />
      ) : (
        <SavePaymentMethodModal
          open={!isPaymentSaved}
          onClose={onCancel}
          onPaymentSaved={handleSavePaymentComplete}
          closeAfterSave={false}
        />
      )}
      {loading && <Loading />}
    </Box>
  );
};
